import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@chakra-ui/icons";
import { Flex, Text } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";

import { ReactComponent as Information } from "../../assets/icons/information.svg";
import ShortlistEmptyState from "./ShortlistEmptyState";
import CandidateListItem from "./CandidateListItem";
import CandidateDetails from "./CandidateDetails";
import ProgressionTabs from "./ProgressionTabs";

import {
  CandidateTriageStatusHired,
} from "../../graphql/jobs";
import { candidateMatchesDeepLinkId } from "../../helpers/utils";

const shortlistTab = "Shortlist";
const hiredTab = "Hired";
const tabItems = [
  { name: shortlistTab, displayText: shortlistTab },
  { name: hiredTab, displayText: hiredTab },
];

const listParamToTab = {
  shortlist: shortlistTab,
  hired: hiredTab,
};

function tabForCandidate(candidateTriage) {
  return candidateTriage?.status === CandidateTriageStatusHired
    ? hiredTab
    : shortlistTab;
}

function ClientShortlistBody({
  tabName,
  clientTriageListForJob,
  moblyzeJob,
  refetch,
}) {
  const filteredTriageList = useMemo(() => {
    if (tabName === shortlistTab) {
      return clientTriageListForJob.filter(
        (candidateTriage) =>
          candidateTriage.status !== CandidateTriageStatusHired
      );
    }
    if (tabName === hiredTab) {
      return clientTriageListForJob.filter(
        (candidateTriage) =>
          candidateTriage.status === CandidateTriageStatusHired
      );
    }
    return [];
  }, [clientTriageListForJob, tabName]);

  const emptyMessage =
    tabName === shortlistTab
      ? `You have no candidates to review.

Keep an eye out for the recruiter to start building the shortlist for this job.

We'll notify you when you have candidates to review.`
      : `No candidates have been hired yet.

Once a candidate is hired they will appear here.`;

  const [searchParams, setSearchParams] = useSearchParams();
  const showCandidateId = searchParams.get("showCandidate");

  const [candidateTriage, setCandidateTriage] = useState(() => {
    if (showCandidateId) {
      const fromUrl = filteredTriageList.find(
        (ct) => candidateMatchesDeepLinkId(ct?.candidate, showCandidateId)
      );
      if (fromUrl) return fromUrl;
    }
    return filteredTriageList?.length > 0 ? filteredTriageList[0] : null;
  });

  // Keep the selected candidate in sync with the (possibly filtered) triage
  // list: prefer the candidate referenced by ?showCandidate=, otherwise keep
  // the existing pick if it's still present, otherwise fall back to the first.
  useEffect(() => {
    if (filteredTriageList.length === 0) {
      if (candidateTriage) setCandidateTriage(null);
      return;
    }
    if (showCandidateId) {
      const fromUrl = filteredTriageList.find(
        (ct) => candidateMatchesDeepLinkId(ct?.candidate, showCandidateId)
      );
      if (fromUrl) {
        if (candidateTriage?.id !== fromUrl.id) {
          setCandidateTriage(fromUrl);
        }
        return;
      }
    }
    if (candidateTriage) {
      const stillPresent = filteredTriageList.find(
        (ct) => ct.id === candidateTriage.id
      );
      if (stillPresent) {
        if (stillPresent !== candidateTriage) setCandidateTriage(stillPresent);
        return;
      }
    }
    setCandidateTriage(filteredTriageList[0]);
  }, [filteredTriageList, candidateTriage, showCandidateId]);

  const handleSetCandidateTriage = useCallback(
    (newCandidateTriage) => {
      setCandidateTriage(newCandidateTriage);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const candidateProfileId = newCandidateTriage?.candidate?.candidateProfileId;
          if (candidateProfileId) {
            next.set("showCandidate", candidateProfileId);
          } else {
            next.delete("showCandidate");
          }
          next.delete("tab");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  if (filteredTriageList.length === 0) {
    return <ShortlistEmptyState message={emptyMessage} />;
  }

  return (
    <Flex gap="50px">
      <CandidateListItem
        setSelect={handleSetCandidateTriage}
        select={candidateTriage}
        candidateTriageList={filteredTriageList}
        criteriaCategories={moblyzeJob?.criteriaCategories || []}
        moblyzeJobId={moblyzeJob?.id}
        allowEditCriteria={false}
        isClient
        showAvailabilityFilter
        jobStartDate={moblyzeJob?.startDate}
      />
      {candidateTriage && (
        <CandidateDetails
          candidateTriage={candidateTriage}
          isClient
          showClientPassAddActions={tabName === shortlistTab}
          moblyzeJob={moblyzeJob}
          refetch={refetch}
        />
      )}
    </Flex>
  );
}

export default function ClientShortlist({
  moblyzeJob,
  clientTriageListForJob,
  refetch,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const listParam = searchParams.get("list");
  const showCandidateId = searchParams.get("showCandidate");
  const lastHandledCandidateRef = useRef(null);

  const [selectedTabItemName, setSelectedTabItemName] = useState(() => {
    if (showCandidateId && clientTriageListForJob) {
      const candidate = clientTriageListForJob.find(
        (ct) => candidateMatchesDeepLinkId(ct?.candidate, showCandidateId)
      );
      if (candidate) {
        lastHandledCandidateRef.current = showCandidateId;
        return tabForCandidate(candidate);
      }
    }
    const fromList = listParam && listParamToTab[listParam.toLowerCase()];
    return fromList || shortlistTab;
  });

  // ?showCandidate= wins over ?list=: a deep link to a candidate jumps to
  // whichever tab contains them. Once the candidate has been resolved (found
  // or not), fall back to ?list= so plain tab links keep working.
  useEffect(() => {
    if (!clientTriageListForJob) return;
    if (showCandidateId) {
      if (lastHandledCandidateRef.current === showCandidateId) {
        return;
      }
      const candidate = clientTriageListForJob.find(
        (ct) => candidateMatchesDeepLinkId(ct?.candidate, showCandidateId)
      );
      if (candidate) {
        const expectedTab = tabForCandidate(candidate);
        if (selectedTabItemName !== expectedTab) {
          setSelectedTabItemName(expectedTab);
        }
        lastHandledCandidateRef.current = showCandidateId;
        return;
      }
      lastHandledCandidateRef.current = showCandidateId;
    }

    const tabFromList = listParam && listParamToTab[listParam.toLowerCase()];
    if (tabFromList && selectedTabItemName !== tabFromList) {
      setSelectedTabItemName(tabFromList);
    }
  }, [listParam, showCandidateId, clientTriageListForJob, selectedTabItemName]);

  const handleTabChange = useCallback(
    (nextTab) => {
      setSelectedTabItemName(nextTab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("list", String(nextTab).toLowerCase());
          next.delete("showCandidate");
          next.delete("tab");
          next.delete("chat");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  if (!moblyzeJob) {
    return null;
  }

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between">
        <ProgressionTabs
          tabs={tabItems}
          selectedTabItemName={selectedTabItemName}
          setSelectedTabItemName={handleTabChange}
          refetch={refetch}
        />
        {clientTriageListForJob.length > 0 &&
          selectedTabItemName === shortlistTab && (
            <Flex alignItems="center" gap="12px">
              <Icon fontSize="24px" as={Information} />
              <Text fontSize="md" fontWeight="normal" lineHeight="6">
                Let us know if you think this candidate is a good fit and we'll
                get you connected!
              </Text>
            </Flex>
          )}
      </Flex>

      <ClientShortlistBody
        tabName={selectedTabItemName}
        clientTriageListForJob={clientTriageListForJob}
        moblyzeJob={moblyzeJob}
        refetch={refetch}
      />
    </>
  );
}
